/**
 * School feature health contracts (plan docs/_archive/2026-10-17-plans-sweep/plans/2026-09-12-2056-feat-school-feature-health-plan.md, KTD12).
 *
 *   GET  /v1/system/schools/:schoolId/feature-health           super-admin
 *   POST /v1/system/schools/:schoolId/feature-health/refresh   super-admin
 *   GET  /internal/feature-health/schools/:schoolId            ops API key
 *   POST /internal/feature-health/schools/:schoolId/refresh    ops API key
 *
 * All four return the same body for the same school: both route families call
 * one read and one refresh, so staleness, the roll-up and criticality are
 * decided once, server-side. Readers carry no feature list and derive nothing
 * the backend already decided (KTD1): display names, order, next actions and
 * each row's detail block all arrive in the response.
 *
 * `:schoolId` is the school's public CUID. Refusals come back in the standard
 * error body (`{ error: { type, message, details } }`) with the type codes in
 * {@link FEATURE_HEALTH_ERROR_TYPES}.
 */

import { z } from 'zod'
import {
  FeatureHealthCheckStatusSchema,
  FeatureHealthEvidenceSchema,
  FeatureHealthPersonaSchema,
  FeatureHealthRollupStateSchema,
  FeatureHealthSecondaryUnitSchema,
  FeatureHealthStateSchema,
  FeatureHealthTriggerSchema
} from '../../schemas/entities/feature-health'

/** Sweep interval; a stored verdict older than two of these reads unknown (R32). */
export const FEATURE_HEALTH_SWEEP_INTERVAL_MINUTES = 30

/** Clears a failing or degraded row needs before it reads ok (R13). */
export const FEATURE_HEALTH_CLEARS_NEEDED = 2

/** Per-school refresh cooldown (R36, KTD12). */
export const FEATURE_HEALTH_REFRESH_COOLDOWN_SECONDS = 60

/** Error `type` codes the feature-health endpoints return beyond the framework's own. */
export const FEATURE_HEALTH_ERROR_TYPES = {
  /** 429 — this school was refreshed less than a minute ago. `details.retryAfterSeconds` says how long to wait. */
  REFRESH_COOLDOWN: 'FEATURE_HEALTH_REFRESH_COOLDOWN',
  /** 429 — four inline refreshes are already running fleet-wide. `details.retryAfterSeconds` is a hint. */
  REFRESH_BUSY: 'FEATURE_HEALTH_REFRESH_BUSY',
  /** 409 — the school is outside the sweep population (deleted or no Blackbaud integration), so refresh is refused. */
  SCHOOL_NOT_SWEPT: 'FEATURE_HEALTH_SCHOOL_NOT_SWEPT'
} as const

/** `error.details` on a 429 refusal. */
export const FeatureHealthRefreshRefusalDetailsSchema = z.object({
  retryAfterSeconds: z.number().int().nonnegative()
})
export type FeatureHealthRefreshRefusalDetails = z.infer<typeof FeatureHealthRefreshRefusalDetailsSchema>

export const FeatureHealthParamsSchema = z.object({
  schoolId: z.string().min(1).describe('School public CUID')
})
export type FeatureHealthParams = z.infer<typeof FeatureHealthParamsSchema>

const isoDateTime = z.string().datetime()

/** A row's secondary number (R10). A null value renders as a dash with `reason`, never as zero. */
export const FeatureHealthSecondarySchema = z.object({
  value: z.number().nullable(),
  unit: FeatureHealthSecondaryUnitSchema,
  /** What the number counts, e.g. "avg hours from Blackbaud publish to feed". */
  label: z.string(),
  /** Why `value` is null; null when the value is present. */
  reason: z.string().nullable()
})
export type FeatureHealthSecondary = z.infer<typeof FeatureHealthSecondarySchema>

/**
 * Clearing progress on a failing or degraded row (R13, R36, AE18): "clearing 1 of 2".
 *
 * - STORED reading (GET, and `row.stored` on a refresh): mirrors the stored
 *   streak. `{ streak, needed }` while the stored state is failing or degraded
 *   and the streak is above zero; otherwise null. Only a sweep advances the
 *   streak (KTD5).
 * - LIVE reading (a refresh's `row.clearing`): a projection. When the stored
 *   state is failing or degraded and this evaluation sees the clear condition,
 *   `{ streak: storedStreak + 1, needed: 2 }`, capped at `needed`. Otherwise
 *   null. The stored streak does not move. So a row failing for a week whose
 *   output lands today refreshes to "clearing 1 of 2" the same day (AE18), and
 *   the stored row stays failing until two sweeps have seen it clear.
 */
export const FeatureHealthClearingSchema = z.object({
  streak: z.number().int().nonnegative(),
  needed: z.number().int().positive()
})
export type FeatureHealthClearing = z.infer<typeof FeatureHealthClearingSchema>

/** One subcomponent of a row, by persona (R3, R22). */
export const FeatureHealthSubcomponentSchema = z.object({
  key: z.string(),
  persona: FeatureHealthPersonaSchema,
  displayName: z.string(),
  state: FeatureHealthStateSchema,
  reason: z.string()
})
export type FeatureHealthSubcomponent = z.infer<typeof FeatureHealthSubcomponentSchema>

/** An enabling switch and whether it was on at the last computation (R22). */
export const FeatureHealthSwitchSchema = z.object({
  scope: z.enum(['global', 'screen']),
  path: z.string(),
  /** Null when never read, or when the config could not be read. */
  on: z.boolean().nullable()
})
export type FeatureHealthSwitch = z.infer<typeof FeatureHealthSwitchSchema>

/** A sync type that feeds the row, with its last retained terminal job (R22). */
export const FeatureHealthFeedingSyncSchema = z.object({
  syncType: z.string(),
  lastTerminalStatus: z.string().nullable(),
  lastTerminalAt: isoDateTime.nullable()
})
export type FeatureHealthFeedingSync = z.infer<typeof FeatureHealthFeedingSyncSchema>

/** A Blackbaud role-coverage module the row needs, and any missing role (R5, R22). */
export const FeatureHealthCoverageModuleSchema = z.object({
  key: z.string(),
  /** Whether a gap in this module blocks the row (true) or only annotates the reason. */
  required: z.boolean(),
  criticality: z.enum(['REQUIRED', 'OPTIONAL']),
  /** Null until a coverage snapshot exists for the school. */
  status: z.enum(['available', 'degraded', 'dead']).nullable(),
  missingRoles: z.array(z.string())
})
export type FeatureHealthCoverageModule = z.infer<typeof FeatureHealthCoverageModuleSchema>

/** Everything the expanded row renders (R22), captured at evaluation time. */
export const FeatureHealthRowDetailSchema = z.object({
  subcomponents: z.array(FeatureHealthSubcomponentSchema),
  switches: z.array(FeatureHealthSwitchSchema),
  syncs: z.array(FeatureHealthFeedingSyncSchema),
  coverageModules: z.array(FeatureHealthCoverageModuleSchema),
  /** Per-source check status at the last computation. Null when no verdict is stored. */
  sources: z.record(z.string(), FeatureHealthCheckStatusSchema).nullable(),
  /** The raw evidence numbers. Null when no verdict is stored. */
  evidence: FeatureHealthEvidenceSchema.nullable()
})
export type FeatureHealthRowDetail = z.infer<typeof FeatureHealthRowDetailSchema>

/**
 * The stored verdict beside a live reading, so a better live reading can say
 * what the stored row still shows. `clearing` here is the STORED streak (see
 * `FeatureHealthClearingSchema`).
 */
export const FeatureHealthStoredReadingSchema = z.object({
  state: FeatureHealthStateSchema,
  clearing: FeatureHealthClearingSchema.nullable(),
  computedAt: isoDateTime.nullable()
})
export type FeatureHealthStoredReading = z.infer<typeof FeatureHealthStoredReadingSchema>

/** One registry feature joined to its verdict, in registry order (R21). */
export const FeatureHealthRowSchema = z.object({
  /** Registry key, e.g. `assignment-grades`. */
  key: z.string(),
  displayName: z.string(),
  order: z.number().int(),
  /** Whether the row's output is synced from Blackbaud (blocked when the integration is down, R33). */
  blackbaudFed: z.boolean(),
  /** Row criticality from its coverage modules: a required blocked row ranks with failing (R15). */
  required: z.boolean(),
  /** Whether the row has an evidence rule; false rows read not-graded (R7). */
  graded: z.boolean(),
  state: FeatureHealthStateSchema,
  reason: z.string(),
  /** Registry-owned next action in plain words (R23); null when the state needs none. */
  nextAction: z.string().nullable(),
  secondary: FeatureHealthSecondarySchema.nullable(),
  computedAt: isoDateTime.nullable(),
  trigger: FeatureHealthTriggerSchema.nullable(),
  /** True when the stored verdict is older than two sweep intervals or absent (R32). */
  stale: z.boolean(),
  /** Stored streak on a stored reading, projection on a live one (see `FeatureHealthClearingSchema`). */
  clearing: FeatureHealthClearingSchema.nullable(),
  /** Present on a live (refresh) reading only: what the stored verdict says. */
  stored: FeatureHealthStoredReadingSchema.nullable(),
  detail: FeatureHealthRowDetailSchema
})
export type FeatureHealthRow = z.infer<typeof FeatureHealthRowSchema>

/** A row named beside the roll-up (R15). */
export const FeatureHealthNamedRowSchema = z.object({
  key: z.string(),
  displayName: z.string(),
  state: FeatureHealthStateSchema,
  required: z.boolean()
})
export type FeatureHealthNamedRow = z.infer<typeof FeatureHealthNamedRowSchema>

/** Worst-of over the contributing rows, with the failing, blocked and unknown rows named (R15). */
export const FeatureHealthRollupSchema = z.object({
  state: FeatureHealthRollupStateSchema,
  /** One line, e.g. "Assignment Grades failing; News Feed blocked". */
  summary: z.string(),
  failing: z.array(FeatureHealthNamedRowSchema),
  blocked: z.array(FeatureHealthNamedRowSchema),
  unknown: z.array(FeatureHealthNamedRowSchema)
})
export type FeatureHealthRollup = z.infer<typeof FeatureHealthRollupSchema>

/** Whether the school is in the sweep population (R32, R35). */
export const FEATURE_HEALTH_SCHOOL_STATUSES = ['swept', 'not-swept'] as const
export const FeatureHealthSchoolStatusSchema = z.enum(FEATURE_HEALTH_SCHOOL_STATUSES)
export type FeatureHealthSchoolStatus = z.infer<typeof FeatureHealthSchoolStatusSchema>

/** `stored` for a GET; `live` for a refresh, whose rows are the raw reading just computed (R36). */
export const FeatureHealthReadingSchema = z.enum(['stored', 'live'])
export type FeatureHealthReading = z.infer<typeof FeatureHealthReadingSchema>

export const FeatureHealthSchoolSchema = z.object({
  school: z.object({
    /** School public CUID. */
    id: z.string(),
    name: z.string(),
    /** Demo and template schools are graded and labelled (R35). */
    kind: z.enum(['live', 'demo', 'template'])
  }),
  status: FeatureHealthSchoolStatusSchema,
  /** Why the school is not swept; null when swept. A not-swept response carries no rows. */
  notSweptReason: z.string().nullable(),
  reading: FeatureHealthReadingSchema,
  rollup: FeatureHealthRollupSchema,
  /** Rows per state, every state present. */
  counts: z.record(FeatureHealthStateSchema, z.number().int().nonnegative()),
  /** Newest sweep tick across the school's stored rows; null when never swept. */
  lastSweepAt: isoDateTime.nullable(),
  /** True when no stored row is newer than two sweep intervals (R32). */
  stale: z.boolean(),
  /** Integration-down banner fields (R33). */
  integration: z.object({
    down: z.boolean(),
    since: isoDateTime.nullable()
  }),
  /** When this response was assembled. */
  generatedAt: isoDateTime,
  rows: z.array(FeatureHealthRowSchema)
})
export type FeatureHealthSchool = z.infer<typeof FeatureHealthSchoolSchema>

/** Body of both GET routes. */
export const GetFeatureHealthResponseSchema = z.object({ data: FeatureHealthSchoolSchema })
export type GetFeatureHealthResponse = z.infer<typeof GetFeatureHealthResponseSchema>

/** Body of both refresh routes: the live reading, stored only through the guarded write (R36). */
export const RefreshFeatureHealthResponseSchema = z.object({ data: FeatureHealthSchoolSchema })
export type RefreshFeatureHealthResponse = z.infer<typeof RefreshFeatureHealthResponseSchema>
